import { nowUtc } from '@/lib/clock';
import { newUuid } from '@/lib/ids';
import { db as defaultDb, type GentleTaskDatabase } from '@/data/db/database';
import type { Attachment, AttachmentMimeType, AttachmentOwnerType, Uuid } from '@/data/db';
import { markDataMutation } from './support';

export interface ProcessedImagePayload {
  blob: Blob;
  thumbnailBlob: Blob;
  mimeType: AttachmentMimeType;
  width: number;
  height: number;
  originalSize: number;
  size: number;
  sha256: string;
}

export interface AddAttachmentInput {
  ownerType: AttachmentOwnerType;
  ownerId: Uuid;
  fileName: string;
  processed: ProcessedImagePayload;
}

export interface AttachmentSummary {
  count: number;
  totalBytes: number;
}

export function createAttachmentRepository(db: GentleTaskDatabase = defaultDb) {
  const writeTables = [db.attachments, db.settings];

  /**
   * 新增圖片。單機版不限制圖片總張數。
   * sha256 只作為備份完整性驗證，**不用於自動去重**：
   * 相同圖片重複上傳會產生兩筆，各佔一個名額。
   */
  async function add(input: AddAttachmentInput): Promise<Attachment> {
    const timestamp = nowUtc();
    const attachment: Attachment = {
      id: newUuid(),
      ownerType: input.ownerType,
      ownerId: input.ownerId,
      fileName: input.fileName,
      mimeType: input.processed.mimeType,
      originalSize: input.processed.originalSize,
      size: input.processed.size,
      width: input.processed.width,
      height: input.processed.height,
      sha256: input.processed.sha256,
      blob: input.processed.blob,
      thumbnailBlob: input.processed.thumbnailBlob,
      createdAt: timestamp,
    };

    await db.transaction('rw', writeTables, async () => {
      await db.attachments.add(attachment);
      await markDataMutation(db, timestamp);
    });

    return attachment;
  }

  /** 圖片刪除直接生效，不進垃圾桶（spec 6.4）。 */
  async function remove(id: Uuid): Promise<void> {
    const timestamp = nowUtc();
    await db.transaction('rw', writeTables, async () => {
      await db.attachments.delete(id);
      await markDataMutation(db, timestamp);
    });
  }

  async function removeMany(ids: readonly Uuid[]): Promise<void> {
    const timestamp = nowUtc();
    await db.transaction('rw', writeTables, async () => {
      await db.attachments.bulkDelete([...ids]);
      await markDataMutation(db, timestamp);
    });
  }

  async function listFor(ownerType: AttachmentOwnerType, ownerId: Uuid): Promise<Attachment[]> {
    return db.attachments.where('[ownerType+ownerId]').equals([ownerType, ownerId]).toArray();
  }

  async function listAll(): Promise<Attachment[]> {
    return db.attachments.toArray();
  }

  async function summarize(): Promise<AttachmentSummary> {
    const all = await db.attachments.toArray();
    const totalBytes = all.reduce((sum, attachment) => sum + attachment.size, 0);
    return {
      count: all.length,
      totalBytes,
    };
  }

  /**
   * 擁有者已經不存在的圖片。
   * 擁有者只是進垃圾桶時**不算孤兒**——還原後圖片要照常顯示。
   */
  async function listOrphans(): Promise<Attachment[]> {
    const [all, taskIds, logIds] = await Promise.all([
      db.attachments.toArray(),
      db.tasks.toArray().then((rows) => new Set(rows.map((row) => row.id))),
      db.followUpLogs.toArray().then((rows) => new Set(rows.map((row) => row.id))),
    ]);

    return all.filter((attachment) =>
      attachment.ownerType === 'task'
        ? !taskIds.has(attachment.ownerId)
        : !logIds.has(attachment.ownerId),
    );
  }

  return { add, remove, removeMany, listFor, listAll, summarize, listOrphans };
}

export type AttachmentRepository = ReturnType<typeof createAttachmentRepository>;
export const attachmentRepository = createAttachmentRepository();
